/**
 * IntegrationsPage - External integrations management
 * ZAPHUB 2.0 - Connect to 30+ external services
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import {
  Search,
  Plus,
  Check,
  Settings,
  Zap,
  Mail,
  MessageSquare,
  FileText,
  Code,
  DollarSign,
  Phone,
  Database,
  Cloud,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Integration, IntegrationType } from '@shared/types';

const INTEGRATION_ICONS: Record<string, any> = {
  SLACK: MessageSquare,
  GMAIL: Mail,
  NOTION: FileText,
  GITHUB: Code,
  JIRA: Settings,
  STRIPE: DollarSign,
  TWILIO: Phone,
  DATABASE: Database,
  AWS_S3: Cloud,
};

export const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      // Mock data - in production, call API
      const mockIntegrations: Integration[] = [
        {
          id: '1',
          type: 'SLACK' as IntegrationType,
          name: 'Slack',
          description: 'Send messages, create channels, manage users',
          icon: '💬',
          category: 'Communication',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'send_message',
              name: 'Send Message',
              description: 'Send a message to a channel or user',
              inputSchema: {},
              outputSchema: {},
            },
            {
              id: 'create_channel',
              name: 'Create Channel',
              description: 'Create a new Slack channel',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          rateLimits: { requestsPerMinute: 60 },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '2',
          type: 'GMAIL' as IntegrationType,
          name: 'Gmail',
          description: 'Send emails, manage inbox, create filters',
          icon: '📧',
          category: 'Email',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'send_email',
              name: 'Send Email',
              description: 'Send an email via Gmail',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          rateLimits: { requestsPerSecond: 1 },
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '3',
          type: 'NOTION' as IntegrationType,
          name: 'Notion',
          description: 'Create pages, update databases, manage content',
          icon: '📝',
          category: 'Productivity',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'create_page',
              name: 'Create Page',
              description: 'Create a new page in Notion',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '4',
          type: 'GITHUB' as IntegrationType,
          name: 'GitHub',
          description: 'Manage repositories, issues, pull requests',
          icon: '🐙',
          category: 'Development',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'create_issue',
              name: 'Create Issue',
              description: 'Create a new issue in a repository',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [
            {
              id: 'new_issue',
              name: 'New Issue',
              description: 'Triggered when a new issue is created',
              outputSchema: {},
            },
          ],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '5',
          type: 'JIRA' as IntegrationType,
          name: 'Jira',
          description: 'Manage projects, issues, and workflows',
          icon: '📊',
          category: 'Project Management',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'create_issue',
              name: 'Create Issue',
              description: 'Create a new Jira issue',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '6',
          type: 'STRIPE' as IntegrationType,
          name: 'Stripe',
          description: 'Process payments, manage subscriptions',
          icon: '💳',
          category: 'Payment',
          authType: 'api_key',
          configSchema: {},
          actions: [
            {
              id: 'create_payment',
              name: 'Create Payment Intent',
              description: 'Create a new payment intent',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [
            {
              id: 'payment_success',
              name: 'Payment Successful',
              description: 'Triggered when a payment succeeds',
              outputSchema: {},
            },
          ],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '7',
          type: 'TWILIO' as IntegrationType,
          name: 'Twilio',
          description: 'Send SMS, make calls, manage phone numbers',
          icon: '📱',
          category: 'Communication',
          authType: 'api_key',
          configSchema: {},
          actions: [
            {
              id: 'send_sms',
              name: 'Send SMS',
              description: 'Send an SMS message',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '8',
          type: 'GOOGLE_SHEETS' as IntegrationType,
          name: 'Google Sheets',
          description: 'Read and write spreadsheet data',
          icon: '📊',
          category: 'Productivity',
          authType: 'oauth2',
          configSchema: {},
          actions: [
            {
              id: 'append_row',
              name: 'Append Row',
              description: 'Append a new row to a spreadsheet',
              inputSchema: {},
              outputSchema: {},
            },
            {
              id: 'read_range',
              name: 'Read Range',
              description: 'Read data from a spreadsheet range',
              inputSchema: {},
              outputSchema: {},
            },
          ],
          triggers: [],
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ];

      setIntegrations(mockIntegrations);
      setConnectedIntegrations(new Set(['1', '2'])); // Mock connected integrations
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (integration: Integration) => {
    setSelectedIntegration(integration);
    setShowConfigDialog(true);
  };

  const handleDisconnect = (integrationId: string) => {
    const integration = integrations.find(i => i.id === integrationId);
    setConnectedIntegrations((prev) => {
      const next = new Set(prev);
      next.delete(integrationId);
      return next;
    });
    toast.success(`Disconnected from ${integration?.name || 'integration'}`);
  };

  const confirmConnection = () => {
    if (selectedIntegration) {
      setConnectedIntegrations((prev) => new Set(prev).add(selectedIntegration.id));
      setShowConfigDialog(false);
      toast.success(`Connected to ${selectedIntegration.name}`);
    }
  };

  const filteredIntegrations = integrations.filter(
    (integration) =>
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = ['All', ...Array.from(new Set(integrations.map((i) => i.category)))];

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Integrations</h1>
        <p className="text-gray-600">
          Connect ZAPHUB with your favorite tools and services
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl">{integrations.length}</div>
              <div className="text-sm text-gray-600">Available Integrations</div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl">{connectedIntegrations.size}</div>
              <div className="text-sm text-gray-600">Connected</div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Plus className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl">{categories.length - 1}</div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Integrations</TabsTrigger>
          <TabsTrigger value="connected">Connected</TabsTrigger>
          <TabsTrigger value="popular">Popular</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map((integration) => {
              const isConnected = connectedIntegrations.has(integration.id);
              const IconComponent = INTEGRATION_ICONS[integration.type] || Settings;

              return (
                <Card key={integration.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl">
                        {integration.icon || <IconComponent className="h-6 w-6" />}
                      </div>
                      <div>
                        <h3 className="text-lg">{integration.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {integration.category}
                        </Badge>
                      </div>
                    </div>
                    {isConnected && (
                      <Badge variant="default" className="bg-green-500">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{integration.description}</p>

                  <div className="mb-4 space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-600">Actions:</span>{' '}
                      <span>{integration.actions.length}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Triggers:</span>{' '}
                      <span>{integration.triggers.length}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Auth:</span>{' '}
                      <Badge variant="outline" className="text-xs">
                        {integration.authType}
                      </Badge>
                    </div>
                  </div>

                  {isConnected ? (
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" size="sm">
                        <Settings className="h-4 w-4 mr-2" />
                        Configure
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDisconnect(integration.id)}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button className="w-full" onClick={() => handleConnect(integration)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Connect
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="connected" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations
              .filter((i) => connectedIntegrations.has(i.id))
              .map((integration) => {
                const IconComponent = INTEGRATION_ICONS[integration.type] || Settings;
                return (
                  <Card key={integration.id} className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl">
                        {integration.icon || <IconComponent className="h-6 w-6" />}
                      </div>
                      <div>
                        <h3 className="text-lg">{integration.name}</h3>
                        <Badge variant="default" className="bg-green-500 text-xs">
                          Active
                        </Badge>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" size="sm">
                      <Settings className="h-4 w-4 mr-2" />
                      Manage
                    </Button>
                  </Card>
                );
              })}
            {integrations.filter((i) => connectedIntegrations.has(i.id)).length === 0 && (
              <div className="col-span-3 text-center py-12 text-gray-500">
                No connected integrations yet
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="popular" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.slice(0, 6).map((integration) => {
              const IconComponent = INTEGRATION_ICONS[integration.type] || Settings;
              return (
                <Card key={integration.id} className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl">
                      {integration.icon || <IconComponent className="h-6 w-6" />}
                    </div>
                    <h3 className="text-lg">{integration.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
                  <Button className="w-full" onClick={() => handleConnect(integration)}>
                    Connect
                  </Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Connection Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Configure your connection to {selectedIntegration?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedIntegration && (
            <div className="space-y-4 py-4">
              {selectedIntegration.authType === 'oauth2' ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-600 mb-4">
                    You will be redirected to {selectedIntegration.name} to authorize ZAPHUB
                  </p>
                  <Button>
                    Continue to {selectedIntegration.name}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>API Key</Label>
                    <Input type="password" placeholder="Enter your API key" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Your API key will be encrypted and stored securely
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmConnection}>
              {selectedIntegration?.authType === 'oauth2' ? 'Authorize' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};